import { useEffect, useState } from "react";
import { CheckCircle2, Circle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { RaceSheet } from "@/pages/FinalSheets";

const STEPS = [
  ["options", "Field size, starters & track options"],
  ["download", "Download race & field details"],
  ["rate", "Scan every horse → Raw Score"],
  ["model", "Run my rating rules"],
  ["done", "Final field"],
];

const Step = ({ n, label, state, detail }) => (
  <li className="flex items-start gap-3" data-testid={`model-step-${n}`} data-state={state}>
    {state === "done" ? <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
      : state === "active" ? <Loader2 className="h-5 w-5 text-amber-400 animate-spin shrink-0" />
      : <Circle className="h-5 w-5 text-slate-700 shrink-0" />}
    <div>
      <div className={`text-sm ${state === "todo" ? "text-slate-500" : "text-white"}`}><span className="num text-slate-500 mr-2">{n}</span>{label}</div>
      {detail && <div className="text-xs num text-slate-400 mt-0.5">{detail}</div>}
    </div>
  </li>
);

export const ModelRun = ({ job, busy }) => {
  const [sheets, setSheets] = useState(null);
  const stage = busy ? job?.stage || "download" : job ? "done" : null;
  const idx = stage ? STEPS.findIndex(([k]) => k === stage) : 0;

  useEffect(() => {
    if (busy || !job?.saved_ids) return setSheets(null);
    const ids = new Set(job.saved_ids);
    api.get("/races", { params: { full: true } }).then((r) => setSheets(r.data.filter((x) => ids.has(x.id))));
  }, [busy, job]);

  const detail = (k) => {
    const p = job?.progress;
    if (k === "download" && p && (stage === "download" || idx > 1)) return stage === "download" ? `${p.done}/${p.total} meetings • ${p.label}` : `${job.races?.length ?? 0} races downloaded`;
    if (k === "rate" && idx > 2 && job?.races) {
      const fs = job.races.filter((r) => r.evaluation.checks.some((c) => c.rule === "first_starters" && !c.passed));
      const out = fs.filter((r) => !r.evaluation.scored).length;
      const scored = job.races.filter((r) => r.evaluation.scored).length;
      return out ? `${scored} races scored • ${out} excluded for first starters` : `${scored} races scored • ${fs.length} with first starters (listed unrated)`;
    }
    if (k === "done" && sheets) return `${sheets.length} final sheet(s) below`;
    return null;
  };

  return (
    <div className="space-y-6">
      <ol className="space-y-3" data-testid="model-steps">
        {STEPS.map(([k, label], i) => (
          <Step key={k} n={i + 1} label={label} detail={detail(k)}
            state={i === 0 || (stage && i < idx) || (stage === "done" && sheets) ? "done" : stage && i === idx ? "active" : "todo"} />
        ))}
      </ol>
      {job?.warning && <div data-testid="import-warning" className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2">{job.warning}</div>}
      {sheets && sheets.length === 0 && <p className="text-sm text-slate-500" data-testid="import-no-races">None of the {job.races?.length ?? 0} races checked meet your selection rules.</p>}
      {sheets && sheets.map((r, i) => <RaceSheet key={r.id} race={r} i={i} />)}
    </div>
  );
};
